import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
// import CheckBox from '@mui/icons-material/CheckBox';
// import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
// import { ReactComponent as LocationIcon } from "../../icons/locationIcon.svg";
import { getChangedAttributes, countEffectivelyMetRequirements, resolveMembershipKeyFromQuery } from '../../../utils/helpers'
import { usStates, USStatesEnum, ProfileTab } from "../../../utils/enums"
import { useCompleteSignIn, useUpdateAccountDetails, useAddCredential, useLoadAccountMemberships, useLoadAccountSubscriptions } from '../../../hooks/authhooks'

import UserAvatar from "../../../components/Desktop/UserAvatar";
import { AccountSchema, CurrentRequirementsSchema, CredentialSchema, AddRequirementStatus, MembershipSchema, ResourceSchema } from '../../../data/types';

// import { useGetPaymentMethods } from '../../hooks/paymenthooks'
import { useGetMemberRequirementsStatus } from '../../../hooks/authhooks'
import { AddCredentialModal, CommunityResourcesModal, RequestMembershipModal } from '../../../molecules/Desktop/AppModal';
import MembershipCard from '../../../components/Desktop/MembershipCard';
import SubscriptionCard from '../../../components/Desktop/SubscriptionCard';
import MembershipCredentialsSkeleton from '../../../components/MembershipCredentialsSkeleton';
import ProfileInformationSkeleton from '../../../components/ProfileInformationSkeleton';
import MySubscriptionsSkeleton from '../../../components/MySubscriptionsSkeleton';
import { toast } from 'sonner';


// type AddRequirementStatus = { status: boolean, requirement?: CurrentRequirementsSchema }


type ProfileTabId = "profile" | "memberships" | "subscriptions"

const getProfileTabFromPath = (pathname: string): ProfileTabId =>
  pathname === "/profile/memberships"
    ? "memberships"
    : pathname === "/profile/subscriptions"
    ? "subscriptions"
    : "profile"


type ProfileTabType = {
  id: ProfileTabId
  details: ProfileTab
  path: string
}

const Profile = () => {

  const profileTabs: ProfileTabType[] = [
    { id: "profile", details: ProfileTab.ProfileInfo, path: "/profile" },
    { id: "memberships", details: ProfileTab.Membership, path: "/profile/memberships" },
    { id: "subscriptions", details: ProfileTab.Subscriptions, path: "/profile/subscriptions" },
  ]

  const { user, isLoadingUser, isFetchingUser } = useCompleteSignIn();
  const loggedinUsername = (user && user?.username) ? user.username : ''
  const loggedinUid = user?.uid ? user?.uid : ""
  const { updateAccount } = useUpdateAccountDetails(loggedinUid)

  const isProfileLoading = isLoadingUser || (isFetchingUser && !user)

  var {
    accountMemberships,
    isLoadingAccountMemberships,
    isFetchingAccountMemberships,
  } = useLoadAccountMemberships(loggedinUid)

  const isMembershipsLoading =
    isLoadingAccountMemberships || (isFetchingAccountMemberships && !accountMemberships)

  var {
    accountSubscriptions,
    isLoadingAccountSubscriptions,
    isFetchingAccountSubscriptions,
  } = useLoadAccountSubscriptions(loggedinUid)

  const isSubscriptionsLoading =
    isLoadingAccountSubscriptions || (isFetchingAccountSubscriptions && !accountSubscriptions)


  const { addMemberCredential } = useAddCredential(loggedinUid)
  // const { memberCredentials } = useGetCredentials(loggedin_username)

  const [profileDetails, setProfileDetails] = useState<AccountSchema>(user ? user : {} as AccountSchema)
  const [userAvatar, setUserAvatar] = useState<string>((user && user?.avatar) ? user.avatar : "")
  const [addingCredential, toggleAddingCredential] = useState<AddRequirementStatus>({ status: false })
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const location = useLocation()
  const navigate = useNavigate()
  const currentLocation = location.pathname

  const [searchParams] = useSearchParams();
  const membershipQuery = searchParams.get('id');

  const defaultTab = getProfileTabFromPath(currentLocation)
  const [activeTab, setActiveTab] = useState<ProfileTabId>(defaultTab)

  useEffect(() => {
    setActiveTab(getProfileTabFromPath(currentLocation))
  }, [currentLocation])

  const { memberRequirementStatuses } = useGetMemberRequirementsStatus(loggedinUid)
  // console.log("memberRequirementsStatus", memberRequirementStatuses)

  // const defaultMemberRequirementsStatus = memberRequirementsStatus
  const [expandedMemberships, setExpandedMemberships] = useState<{ [key: string]: {
    isExpanded: boolean, 
    requirementsStatus: CurrentRequirementsSchema[],
    totalRequirementsCount: number,
    metRequirementsCount: number
  } }>({});
  // console.log("expandedMemberships", expandedMemberships)

  const profileFirstName = profileDetails?.first_name ?? '';
  const profileLastName = profileDetails?.last_name ?? '';

  useEffect(() => {
    document.title = `Profile | ${profileFirstName} ${profileLastName}`.trim();

    // // Check if mobile view
    // const checkMobile = () => {
    //   setIsMobile(window.innerWidth < 768);
    // };
    
    // checkMobile();
    // window.addEventListener('resize', checkMobile);
    
    // return () => window.removeEventListener('resize', checkMobile);
  }, [profileFirstName, profileLastName]);

  useEffect(() => {
    if (accountMemberships && memberRequirementStatuses) {
      const defaultExpandedMembershipKey =
        activeTab === 'memberships'
          ? resolveMembershipKeyFromQuery(membershipQuery, accountMemberships)
          : null;

      setExpandedMemberships(prev => {
        const newExpandedMemberships = { ...prev }
        let hasChanges = false

        accountMemberships?.forEach((membership) => {
          if (membership.community_id) {
            const requirementsStatus = memberRequirementStatuses[membership.community_id] || []
            const totalRequirementsCount = requirementsStatus.filter((item) => (item.is_required === true)).length
            const metRequirementsCount = countEffectivelyMetRequirements(requirementsStatus);
            
            const currentState = newExpandedMemberships[membership.community_id]
            const shouldExpandFromQuery =
              !!defaultExpandedMembershipKey &&
              membership.community_id === defaultExpandedMembershipKey
            const newState = { 
              isExpanded: currentState?.isExpanded ?? shouldExpandFromQuery, 
              requirementsStatus,
              totalRequirementsCount,
              metRequirementsCount
            }

            if (JSON.stringify(currentState) !== JSON.stringify(newState)) {
              hasChanges = true
              newExpandedMemberships[membership.community_id] = newState
            }
          }
        })

        return hasChanges ? newExpandedMemberships : prev
      })
    }
  }, [accountMemberships, memberRequirementStatuses, activeTab, membershipQuery])


  const handleUpdateAccount = async () => {
    let originalData = user ? user : {} as AccountSchema
    let changedData = getChangedAttributes(originalData, profileDetails)
    if (profileDetails && profileDetails !== user && changedData && Object.keys(changedData).length > 0) {
      try {
        await updateAccount(changedData)
        toast.success("Profile updated successfully", {
          description: "Your profile information has been saved.",
          duration: 5000,
          position: "top-right",
          className: "bg-green-500 text-white",
          icon: "🎉",
        })
      } catch {
        toast.error("Failed to update profile", {
          description: "Please try again.",
          duration: 5000,
          position: "top-right",
          className: "bg-red-500 text-white",
          icon: "🚫",
        })
      }
    }
  }

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault()
    const { name, value } = e.target
    if (name && (value || value === "")) {
      setProfileDetails(
        prev => {
          return { ...prev, [name]: value }
        }
      )
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target;
    if (name && files && files.length > 0) {
      let file_data = files[0];
      setProfileDetails(prev => ({
        ...prev,
        [name]: file_data,
      }))
    }
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const imgType = event.target.name;
    
    if (!file) {
      if (imgType === "avatar") {
        setUserAvatar("");
      }
      return;
    }

    // Read the selected image file as a data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (imgType === "avatar" && result) {
        setUserAvatar(result);
      }
      // Reset the input value after processing so the same file can be selected again
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    };
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      // Reset the input value even on error
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
    handleFileChange(event);
  };

  const handleRemoveAvatar = () => {
    setUserAvatar("");
    setProfileDetails(prev => ({
      ...prev,
      'avatar': '',
    }))
  }

  const first_name = profileDetails?.first_name ? profileDetails.first_name : ''
  const last_name = profileDetails?.last_name ? profileDetails.last_name : ''
  const address = profileDetails?.address ? profileDetails.address : ''
  const city = profileDetails?.city ? profileDetails.city : ''
  const state = profileDetails?.state ? profileDetails.state : ''
  const zipcode = profileDetails?.zipcode ? profileDetails.zipcode : ''

  // const { paymentMethods } = useGetPaymentMethods(loggedin_username)
  // const communityRequirements = accountMemberships?.map((membership) => membership.requirements)
  //   .flat()
  //   .filter((req): req is RequirementSchema => req !== undefined) || []
  // const { membershipRequirementsStatus } = useFetchRequirementsLookUp(communityRequirements, memberCredentials ? memberCredentials : [], paymentMethods)


  const handleSubmitCredential = (credential: CredentialSchema, communityId: string) => {
    if (loggedinUsername && credential) {
      credential.username = loggedinUsername;
      addMemberCredential(communityId, credential);
    }
  }



  const toggleMembership = (communityId: string) => {
    setExpandedMemberships(prev => ({
      ...prev,
      [communityId]: {
        ...prev[communityId],
        isExpanded: !prev[communityId]?.isExpanded
      }
    }));
  };

  const [communityResourceID, setCommunityResourceID] = useState("");
  const [viewCommunityResources, setViewCommunityResources] = useState(false);
  const [membershipModal, toggleMembershipModal] = useState<null | "request" | "manage">(null);
  const [selectedMembership, setSelectedMembership] = useState<MembershipSchema | null>(null);
  // const [isMobile, setIsMobile] = useState(false);

  const handleSetViewCommunityResources = (communityId: string, isOpen: boolean) => {
    setViewCommunityResources(isOpen);
    setCommunityResourceID(communityId);
  };

  const handleManageMembership = (membership: MembershipSchema) => {
    setSelectedMembership(membership);
    toggleMembershipModal("manage");
  };

  const membershipToResourceDetail = (membership: MembershipSchema): ResourceSchema => ({
    community_id: membership.community_id,
    id: membership.resource_id,
    name: membership.name,
    logo: membership.logo,
    description: membership.description,
    requires_membership: true,
  });

  // const handleSubmitMembershipRequest = async (
  //   _resource: ResourceSchema,
  //   _resourceType: ResourceType,
  //   _booking: ResourceBookingSchema
  // ) => false;

  // console.log("profileDetails", profileDetails)
  // console.log("accountMemberships", accountMemberships)
  return (
    <>
      <div className="w-full left-0 flex font-open-sans min-h-screen overflow-y-scroll relative top-0 justify-center bg-gradient-to-b from-[#F5F5F5] pb-6 pt-48 md:pt-8 backdrop-blur-2xl bg-[#F5F5F5]">
        <div className="flex flex-col w-11/12 sm:w-10/12 min-h-[1300px] sm:min-h-[2000px] relative items-start py-6 bg-white">
          <div className="w-10/12 mx-auto my-5">
            <p className="font-raleway text-[20px] text-black font-bold my-5">My Profile</p>

            {/* Tab Navigation */}
            <div className="flex flex-row gap-4 mb-8 overflow-scroll">
              {profileTabs.map((tab, i) => (
                <button
                  key={i}
                  className={`px-4 py-2 md:px-6 rounded-md font-raleway font-normal w-[100px] text-[12px] md:text-[15px] text-left md:w-auto  ${activeTab === tab.id ? 'bg-primaryDark-0 text-white font-semibold' : 'bg-white text-black border-[#E9E9E9]'}`}
                  onClick={() => {
                    setActiveTab(tab.id)
                    navigate(`${tab.path}`)
                  }}
                >
                  {tab.details}
                </button>
              ))}
            </div>

            {/* Profile Card */}
            {activeTab === "profile" && (
              isProfileLoading ? (
                <ProfileInformationSkeleton variant="desktop" />
              ) : (
              <>
                <div className='flex flex-col w-full border-[1px] border-[#E9E9E9] rounded-xl my-5 p-10 bg-white'>
                <div className="flex flex-col sm:flex-row items-center rounded-xl   bg-white">
                  <div className="flex-shrink-0 flex items-center justify-center w-[100px] h-[100px] mr-8">
                    {userAvatar ? (
                      <div className="flex border-[#E9E9E9] border-[1px] rounded-full h-[85px] w-[85px] items-center justify-center">
                        <img
                          src={userAvatar}
                          alt="User Avatar"
                          className="h-[72px] w-[72px] rounded-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                    ) : (
                      <div className="flex border-[#E9E9E9] border-[1px] rounded-full h-[85px] w-[85px] items-center justify-center">
                        <UserAvatar
                          firstName={user?.first_name}
                          lastName={user?.last_name}
                          username={user?.username}
                          email={user?.email}
                          className="h-[72px] w-[72px] m-auto font-open-sans rounded-full text-[30px]"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col flex-1 items-start justify-center">
                    <p className="text-[18px] text-black font-medium font-raleway mb-1">Hi, {(user && user?.first_name) ? user.first_name : ''}</p>
                    <p className="text-[14px] font-open-sans text-[#4D4D4D] mb-2">{user?.email || 'No email address yet'}</p>
                    <div className="flex flex-row gap-4 mt-2">
                      <label 
                        htmlFor="image-input-avatar" 
                        className="text-[14px] font-raleway text-[#013847] hover:text-primaryDark-300 font-bold cursor-pointer"
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        Update your profile picture
                      </label>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                        id="image-input-avatar"
                        name="avatar"
                        data-testid="avatar-input"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className={`text-[12px] font-raleway ${userAvatar ? 'text-[#D42620] cursor-pointer' : 'text-[#B3B3B3]'} border-[#E9E9E9] border-0 font-medium rounded-md`}
                      >
                        Remove Profile Picture
                      </button>
                    </div>
                  </div>
                </div>

                {/* Profile Information Form */}
                <div className="bg-white">
                  <div>
                    {/* <p className="text-[18px] font-raleway text-black font-semibold">Profile Information</p>
                    <p className="text-[14px] font-open-sans text-[#808080] mb-6 mt-2">
                      Add your first and last name to keep your profile up-to-date.
                    </p> */}
                    <form onSubmit={e => { e.preventDefault(); handleUpdateAccount(); }}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="flex flex-col text-[14px] font-raleway text-black font-semibold">
                          First Name
                          <input type="text" name="first_name" value={first_name} onChange={handleChangeInput} placeholder="Enter your first name" className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[14px] text-[#808080] font-normal font-open-sans" />
                        </div>
                        <div className="flex flex-col text-[14px] font-raleway text-black font-semibold">
                          Last Name
                          <input type="text" name="last_name" value={last_name} onChange={handleChangeInput} placeholder="Enter your last name" className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[14px] text-[#808080] font-normal font-open-sans" />
                        </div>
                        <div className="flex flex-col text-[14px] font-raleway text-black font-semibold sm:col-span-2">
                          Address
                          <input type="text" name="address" value={address} onChange={handleChangeInput} placeholder="Enter your address" className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[14px] text-[#808080] font-normal font-open-sans" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:col-span-2">
                          <div className="flex flex-col text-[14px] font-raleway text-black font-semibold">
                            City
                            <input type="text" name="city" value={city} onChange={handleChangeInput} placeholder="Enter your city" className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[14px] text-[#808080] font-normal font-open-sans" />
                          </div>
                          <div className="flex flex-col text-[14px] font-raleway text-black font-semibold">
                            State
                            <select
                              name="state"
                              value={state}
                              onChange={handleChangeInput}
                              className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[14px] text-[#808080] font-normal font-open-sans"
                            >
                              <option value="">-- Select your state --</option>
                              {(usStates && usStates.length > 0) && usStates.map((state: string, i: number) => (
                                <option value={USStatesEnum[state]?.abbreviation} key={i}>{USStatesEnum[state]?.fullName}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex flex-col text-[14px] font-raleway text-black font-semibold">
                            Zipcode
                            <input type="text" name="zipcode" value={zipcode} onChange={handleChangeInput} placeholder="Enter your zipcode" className="border-[1px] border-[#E9E9E9] rounded-md my-2 sm:h-[50px] text-[14px] text-[#808080] font-normal font-open-sans" />
                          </div>
                        </div>
                      </div>
                      <button type="submit" className="mt-8 bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-[14px] rounded-md border-0 py-[12px]">Update Profile Information</button>
                    </form>
                  </div>
                </div>
                </div>
              </>
              )
            )}

            {/* Membership Credentials Tab */}
            {activeTab === "memberships" && (
              <div className="my-5">
                {isMembershipsLoading ? (
                  <MembershipCredentialsSkeleton variant="desktop" />
                ) : (
                  <div className="rounded-xl h-auto bg-white relative">
                    <div className='flex flex-col w-full'>
                      <p className="text-[14px] font-raleway text-[#8A8A8A] font-medium mb-6">Showing all memberships ({(accountMemberships && accountMemberships.length > 0) ? accountMemberships.length : 0})</p>
                    </div>
                    <div className='flex flex-col w-full gap-4'>
                      {accountMemberships && accountMemberships.map((membership, i) => {
                        return (
                          <MembershipCard 
                            key={i}
                            membership={membership} 
                            allMembershipCount={accountMemberships?.length || 0}
                            expandedMemberships={expandedMemberships}
                            membershipQuery={membershipQuery}
                            toggleMembership={toggleMembership} 
                            toggleAddingCredential={toggleAddingCredential} 
                            handleSetViewCommunityResources={handleSetViewCommunityResources}
                            handleManageMembership={handleManageMembership}
                          />
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* My Subscriptions Tab */}
            {activeTab === "subscriptions" && (
              isSubscriptionsLoading ? (
                <MySubscriptionsSkeleton variant="desktop" />
              ) : (
              <div className="rounded-xl h-auto my-5 bg-white">
                <div className="flex flex-col w-full">
                  <p className="text-[14px] font-raleway text-[#8A8A8A] font-medium mb-6">
                    Showing all subscriptions ({accountSubscriptions?.length || 0})
                  </p>
                </div>
                {Array.isArray(accountSubscriptions) && accountSubscriptions.length > 0 ? (
                  <div className="flex flex-col w-full gap-4">
                    {accountSubscriptions.map((subscription, i) => (
                      <SubscriptionCard
                        key={subscription.id || `${subscription.community_id}-${subscription.plan_id}-${i}`}
                        subscription={subscription}
                        onViewResources={(communityId) =>
                          handleSetViewCommunityResources(communityId, true)
                        }
                      />
                    ))}
                  </div>
                ) : (
                <div className="flex flex-col items-center justify-center min-h-[200px] border border-[#E9E9E9] rounded-xl p-10">
                  <p className="text-[#8A8A8A] text-sm">No subscriptions to show</p>
                </div>
                )}
              </div>
              )
            )}
          </div>
        </div>
      </div>

      <CommunityResourcesModal isOpen={viewCommunityResources} communityId={communityResourceID} closeAppModal={() => setViewCommunityResources(false)} />
      <AddCredentialModal isOpen={addingCredential.status} closeAppModal={() => toggleAddingCredential({ status: false })} handleSubmitCredential={handleSubmitCredential} requirement={addingCredential.requirement as CurrentRequirementsSchema} />
      {selectedMembership && (
        <RequestMembershipModal
          formType={membershipModal}
          resourceDetail={membershipToResourceDetail(selectedMembership)}
          // handleSubmitRequest={handleSubmitMembershipRequest}
          closeAppModal={() => {
            toggleMembershipModal(null);
            setSelectedMembership(null);
          }}
        />
      )}
    </>
  )
}


// type ExpandedMembershipSchema = {
//   isExpanded: boolean;
//   requirementsStatus: CurrentRequirementsSchema[];
//   totalRequirementsCount: number;
//   metRequirementsCount: number;
// }


// type MembershipCardProps = {
//   membership: MembershipSchema
//   // isActive: boolean
//   expandedMemberships: {[key: string]: ExpandedMembershipSchema }
//   toggleMembership: (communityId: string) => void
//   toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
// }

// export const MembershipCard: React.FC<MembershipCardProps> = ({ membership, expandedMemberships, toggleMembership, toggleAddingCredential }) => {
//   const membershipKey = membership.community_id || ``;
//   const expandedMembership = expandedMemberships[membershipKey];
//   return (
//     <div className="border-[1px]  border-[#E9E9E9] rounded-xl overflow-hidden">
//       <div
//         // onClick={() => toggleMembership(membershipKey)}
//         className={`flex flex-col md:flex-row md:items-center gap-5 md:gap-0 justify-between shadow-sm md:h-[170px] p-4 border-0 cursor-pointer hover:bg-gray-50 transition-colors ${expandedMembership?.isExpanded ? 'bg-[#FAFAFA] rounded-b-none' : 'bg-white'}`}
//       >
//         {/* Logo */}
//         <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-white rounded-lg border mr-6">
//           <img src={membership.logo} alt={membership.name} className="h-10 w-10 object-contain" />
//         </div>
//         {/* Info */}
//         <div className="flex flex-col flex-1 min-w-0 gap-5 md:gap-0">
//           <div className="flex items-center gap-2">
//             <span className="font-semibold font-raleway text-base text-black">{membership.name}</span>
//             {
//               membership.status && (
//                 <span 
//                   className="font-raleway font-medium text-xs ml-2 px-2 py-1 rounded-md"
//                   style={{
//                     backgroundColor: getMembershipStatusColor(membership.status || 'unknown').bgColor,
//                     color: getMembershipStatusColor(membership.status || 'unknown').primaryTextColor
//                   }}
//                 >
//                   {capitalizeString(membership.status)}
//                 </span>
//               )
//             }
//           </div>
//           <span className="text-sm text-[#4D4D4D] mt-1">{(!membership?.description || membership?.description?.length < 300) ? membership.description : `${membership.description.slice(0, 300)}...`}</span>
//           <div className="flex flex-row gap-2 mt-2">
//             <span className="bg-white md:h-[35px] font-raleway items-center flex border-[1px] border-[#E9E9E9] text-[#4D4D4D] px-3 py-1 rounded-md text-xs font-medium">
//               {membership.plan_name} Plan (${styleThousandCommas(membership?.plan_rate || 0)} {membership.plan_recurrence && `/ ${planRecurrenceMap[membership.plan_recurrence as keyof typeof planRecurrenceMap]}`})
//             </span>
//             <span className="bg-white md:h-[35px] font-raleway items-center flex border-[1px] border-[#E9E9E9] text-[#4D4D4D] px-3 py-1 rounded-md text-xs font-medium">
//               {expandedMembership?.metRequirementsCount || 0}/{expandedMembership?.totalRequirementsCount || 0} Requirements Met
//             </span>
//           </div>
//         </div>
//         {/* View Requirements Button */}
//         <div className="flex flex-col items-end">
//           <button
//             className="font-raleway border-[1px] border-[#E9E9E9] rounded-md px-4 py-2 text-sm place-self-center font-semibold text-gray-700 hover:bg-gray-100"
//             onClick={() => toggleMembership(membershipKey)}
//           >
//             View Requirements

//             {expandedMembership?.isExpanded ? (
//               <KeyboardArrowUpIcon className="text-[#A1A1A1] ml-1 my-auto" />
//             ) : (
//               <KeyboardArrowDownIcon className="text-[#A1A1A1] ml-1 my-auto" />
//             )}
//           </button>
//         </div>
//       </div>

//       {expandedMembership?.isExpanded && (
//         <div className="p-4 border-t border-[#E9E9E9]">
//           <RequiredCredentials 
//             membershipRequirementsStatus={expandedMembership.requirementsStatus} 
//             metRequirementsCount={expandedMembership.metRequirementsCount} 
//             totalRequirementsCount={expandedMembership.totalRequirementsCount} 
//             toggleAddingCredential={toggleAddingCredential} 
//           />
//         </div>
//       )}
//     </div>
//   )
// }


// type RequiredCredentialsProps = {
//   membershipRequirementsStatus: CurrentRequirementsSchema[]
//   metRequirementsCount: number
//   totalRequirementsCount: number
//   toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
// }

// const RequiredCredentials: React.FC<RequiredCredentialsProps> = ({ membershipRequirementsStatus, metRequirementsCount, totalRequirementsCount, toggleAddingCredential }) => {
//   return (
//     <>
//       <div className="flex flex-row justify-between items-center w-full">
//         <p className="text-[14px] font-open-sans text-[#808080] mb-4 mr-auto my-auto">
//           {(membershipRequirementsStatus) ? `Complete your membership by adding required credentials.` : `No credentials required for this membership.`}
//         </p>
//         {(totalRequirementsCount > 0) && (
//         <div className="flex items-center mb-4 ml-auto my-auto">
//           <div className="flex flex-col w-full">
//             <p className="text-[14px] font-raleway text-black text-right font-medium m-1">Progress Completion</p>
//             <div className="relative w-[140px] h-2.5 rounded-md">
//               <div className="absolute top-0 left-0 h-2.5 w-full bg-gray-300 rounded-md"></div>
//               <div
//               className="absolute top-0 left-0 h-2.5 bg-green-700 rounded-md"
//               style={{ width: `${(metRequirementsCount / totalRequirementsCount) * 100}%` }}
//               ></div>
//             </div>
//           </div>
//           <p className="text-sm pl-2 font-raleway font-medium">{totalRequirementsCount ? Math.round((metRequirementsCount / totalRequirementsCount) * 100) : 0}%</p>
//         </div>
//         )}
//       </div>
//       <div className="flex flex-col gap-3">
//         {membershipRequirementsStatus && membershipRequirementsStatus.map((requirement, i) => {
//           return (requirement.is_required) && (
//             <div key={i} className="flex flex-row border-[1px] border-[#E9E9E9] rounded-xl p-4 bg-white hover:bg-gray-50 transition-colors">
//               {(requirement.is_met) ? (
//                 <CheckBox className="my-auto text-[#0F973D]" />
//               ) : (
//                 <CheckBoxOutlineBlankIcon className="my-auto text-[#CFCFCF]" />
//               )}
//               <div className="my-auto mx-4 w-full">
//                 <p className="text-[14px] font-raleway text-black font-semibold">{requirement.name}</p>
//                 <p className="text-[12px] font-open-sans text-[#4D4D4D] mt-1">
//                   {requirement.description}
//                 </p>
//               </div>
//               <RequirementButton requirement={requirement} toggleAddingCredential={toggleAddingCredential} />
//             </div>
//           )
//         })}
//       </div>
//     </>
//   )
// }


// type RequirementButtonProps = {
//   requirement: CurrentRequirementsSchema
//   toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>
// }

// const RequirementButton: React.FC<RequirementButtonProps> = ({ requirement, toggleAddingCredential }) => {
//   const navigate = useNavigate()
//   return (
//     <div className='flex flex-row w-full'>
//       {
//         (requirement.code === "PAY") ? (
//           <button onClick={() => navigate('/wallet')} className='bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-[14px] rounded-md border-0 ml-auto h-[43px] my-auto mx-2'>Go to Payment Method</button>
//         ) : (
//             <div className='ml-auto my-auto'>
//               {
//                 (requirement.is_met) && (<button className='bg-white hover:bg-gray-300 border-[#E9E9E9] border-[1px] text-[#D42620] font-raleway font-bold text-[14px] rounded-md h-[43px] my-auto mx-2'>Remove Photo</button>)
//               }
//               <button onClick={() => toggleAddingCredential({ status: true, requirement })} className='bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-[14px] rounded-md border-0 ml-auto h-[43px] my-auto mx-2'>Upload</button>
//             </div>
//         )
//       }
//     </div>
//   )
// }

export default Profile